import { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useSearchParams } from 'react-router-dom'
import { createContent } from '../../graphql/mutation'
import { getAllTags, getAllSections, getAllStyles, getAllPromptsAdmin } from '../../graphql/queries'
import {
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Snackbar,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Divider,
  Avatar,
  Autocomplete,
  Modal
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import ArticleIcon from '@mui/icons-material/Article'
import SaveIcon from '@mui/icons-material/Save'
import ImageIcon from '@mui/icons-material/Image'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import ImageUploadField from '../../components/imageUpload'
import { uploadToS3 } from '../../utils/uploadToS3'

export default function CreateContentPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [content, setContent] = useState({
    contentId: '',
    contentType: 'PROMPT',
    videoUrl: '',
    banner: '',
    gifUrl: '',
    designType: 'MEDIUM',
    tagId: '',
    rowNumber: 1,
    sectionId: '',
    styleId: '',
    promptId: ''
  })

  const [selectedEntity, setSelectedEntity] = useState(null)

  // File upload states
  const [bannerFile, setBannerFile] = useState(null)
  const [videoFile, setVideoFile] = useState(null)
  const [gifFile, setGifFile] = useState(null)
  const [modalImageSrc, setModalImageSrc] = useState('')
  const [openImageModal, setOpenImageModal] = useState(false)

  // Pre-fill tagId from URL parameter
  useEffect(() => {
    const tagIdFromUrl = searchParams.get('tagId')
    if (tagIdFromUrl) {
      setContent(prev => ({ ...prev, tagId: tagIdFromUrl }))
    }
  }, [searchParams])

  const [progress, setProgress] = useState(false)
  const [snackBarOpen, setSnackBarOpen] = useState(false)
  const [snackBarMessage, setSnackBarMessage] = useState(null)
  const [snackBarSeverity, setSnackBarSeverity] = useState('success')

  const { data: tagsData } = useQuery(getAllTags)
  const { data: sectionsData } = useQuery(getAllSections)
  const { data: stylesData } = useQuery(getAllStyles)
  const { data: promptsData } = useQuery(getAllPromptsAdmin)

  const handleChange = (field, value) => {
    setContent(prev => ({
      ...prev,
      [field]: value
    }))

    // Clear main entity selection when changing content type
    if (field === 'contentType') {
      setSelectedEntity(null)
      setContent(prev => ({
        ...prev,
        contentId: ''
        // Keep optional relationship fields (sectionId, styleId, promptId) intact
      }))
    }
  }

  const handleEntitySelect = (entity) => {
    setSelectedEntity(entity)
    if (entity) {
      // Auto-fill only the contentId - relationships are set independently in Additional Options
      setContent(prev => ({
        ...prev,
        contentId: entity.id
      }))
    } else {
      // Clear only the contentId when entity is deselected
      setContent(prev => ({
        ...prev,
        contentId: ''
      }))
    }
  }

  // File upload handlers
  const handleImageClick = (src) => {
    setModalImageSrc(src)
    setOpenImageModal(true)
  }

  const handleBannerFileSelected = (e) => {
    const selectedFile = e.target.files[0]
    setBannerFile(selectedFile)
  }

  const handleVideoFileSelected = (e) => {
    const selectedFile = e.target.files[0]
    setVideoFile(selectedFile)
  }

  const handleGifFileSelected = (e) => {
    const selectedFile = e.target.files[0]
    setGifFile(selectedFile)
  }

  const handleDeleteBanner = () => {
    setBannerFile(null)
    setContent(prev => ({ ...prev, banner: '' }))
  }

  const handleDeleteVideo = () => {
    setVideoFile(null)
    setContent(prev => ({ ...prev, videoUrl: '' }))
  }

  const handleDeleteGif = () => {
    setGifFile(null)
    setContent(prev => ({ ...prev, gifUrl: '' }))
  }

  const [createContentMutation] = useMutation(createContent)

  const handleCreateContent = async () => {
    // Validate that an entity is selected
    if (!content.contentId || !content.contentId.trim()) {
      setSnackBarMessage('Please select a prompt, section, or style for this content')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    setProgress(true)
    try {
      // Upload files to S3 if they exist
      const s3BannerUrl = bannerFile ? await uploadToS3(bannerFile, 'img') : (content.banner || '')
      const s3VideoUrl = videoFile ? await uploadToS3(videoFile, 'video') : (content.videoUrl || '')
      const s3GifUrl = gifFile ? await uploadToS3(gifFile, 'gif') : (content.gifUrl || '')

      const variables = {
        contentType: content.contentType,
        designType: content.designType
      }

      // Only include non-empty values
      if (content.contentId && content.contentId.trim()) {
        variables.contentId = content.contentId.trim()
      }
      if (s3VideoUrl) {
        variables.videoUrl = s3VideoUrl
      }
      if (s3BannerUrl) {
        variables.banner = s3BannerUrl
      }
      if (s3GifUrl) {
        variables.gifUrl = s3GifUrl
      }
      if (content.tagId && content.tagId.trim()) {
        variables.tagId = content.tagId.trim()
      }
      if (content.rowNumber) {
        variables.rowNumber = Number(content.rowNumber)
      }
      if (content.sectionId && content.sectionId.trim()) {
        variables.sectionId = content.sectionId.trim()
      }
      if (content.styleId && content.styleId.trim()) {
        variables.styleId = content.styleId.trim()
      }
      if (content.promptId && content.promptId.trim()) {
        variables.promptId = content.promptId.trim()
      }

      const response = await createContentMutation({
        variables
      })

      if (response.data) {
        setSnackBarMessage('Content created successfully!')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        
        // Navigate to the new content's detail page
        setTimeout(() => {
          navigate(`/content/${response.data.createContent.id}`)
        }, 1500)
      }
    } catch (error) {
      setSnackBarMessage(`Error creating content: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleSnackBarClose = () => {
    setSnackBarOpen(false)
  }

  const getContentTypeColor = (type) => {
    switch (type) {
      case 'PROMPT': return 'primary'
      case 'STYLE': return 'secondary'
      case 'SECTION': return 'success'
      default: return 'default'
    }
  }

  const getDesignTypeColor = (type) => {
    switch (type) {
      case 'SMALL': return 'default'
      case 'MEDIUM': return 'primary'
      case 'LARGE': return 'secondary'
      case 'BANNER': return 'success'
      case 'VIDEO': return 'warning'
      case 'CIRCLE': return 'info'
      case 'PORTRAIT': return 'error'
      case 'SQUARE': return 'default'
      default: return 'default'
    }
  }

  const availableTags = tagsData?.getAllTags || []
  const availableSections = sectionsData?.getAllSections || []
  const availableStyles = stylesData?.getAllStylesAdmin?.styles || []
  const availablePrompts = promptsData?.getAllPromptsAdmin?.prompts || []

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 4, 
      maxWidth: '900px', 
      mx: 'auto', 
      px: 2, 
      py: 2 
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <ArticleIcon fontSize="large" sx={{ color: '#1976d2' }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          Create New Content
        </Typography>
      </Box>

      {/* Description */}
      <Box>
        <Typography variant="body1" color="text.secondary">
          Create a new content item that defines display configuration for prompts, styles, or sections. 
          Content can include banners, videos, and various design types.
        </Typography>
      </Box>

      {/* Main Content */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Grid container spacing={3}>
          {/* Basic Info */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Content Type</InputLabel>
              <Select
                value={content.contentType}
                label="Content Type"
                onChange={(e) => handleChange('contentType', e.target.value)}
              >
                <MenuItem value="PROMPT">Prompt</MenuItem>
                <MenuItem value="STYLE">Style</MenuItem>
                <MenuItem value="SECTION">Section</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Type of content this item represents
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Design Type</InputLabel>
              <Select
                value={content.designType}
                label="Design Type"
                onChange={(e) => handleChange('designType', e.target.value)}
              >
                <MenuItem value="SMALL">Small</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="LARGE">Large</MenuItem>
                <MenuItem value="BANNER">Banner</MenuItem>
                <MenuItem value="VIDEO">Video</MenuItem>
                <MenuItem value="CIRCLE">Circle</MenuItem>
                <MenuItem value="PORTRAIT">Portrait</MenuItem>
                <MenuItem value="SQUARE">Square</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Visual design type for display
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Row Number"
              type="number"
              value={content.rowNumber}
              onChange={(e) => handleChange('rowNumber', parseInt(e.target.value) || 1)}
              required
              inputProps={{ min: 1 }}
            />
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Order position for this content item
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">Selected Types:</Typography>
              <Chip 
                label={content.contentType} 
                color={getContentTypeColor(content.contentType)}
                size="small"
                variant="outlined"
              />
              <Chip 
                label={content.designType} 
                color={getDesignTypeColor(content.designType)}
                size="small"
                variant="filled"
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            {content.contentType === 'PROMPT' && (
              <Autocomplete
                fullWidth
                options={availablePrompts}
                getOptionLabel={(option) => option.name || 'Unnamed Prompt'}
                value={selectedEntity}
                onChange={(event, newValue) => handleEntitySelect(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Prompt *"
                    helperText="Select a prompt to associate with this content. Content ID will be auto-filled."
                    required
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
            {content.contentType === 'SECTION' && (
              <Autocomplete
                fullWidth
                options={availableSections}
                getOptionLabel={(option) => option.name}
                value={selectedEntity}
                onChange={(event, newValue) => handleEntitySelect(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Section *"
                    helperText="Select a section to associate with this content. Content ID will be auto-filled."
                    required
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
            {content.contentType === 'STYLE' && (
              <Autocomplete
                fullWidth
                options={availableStyles}
                getOptionLabel={(option) => option.name}
                value={selectedEntity}
                onChange={(event, newValue) => handleEntitySelect(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Style *"
                    helperText="Select a style to associate with this content. Content ID will be auto-filled."
                    required
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
          </Grid>

          {content.contentId && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Content ID (Auto-filled)"
                value={content.contentId}
                disabled
                helperText="This field is automatically filled based on your selection above"
              />
            </Grid>
          )}

          {/* Media */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Media Content (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload media files or provide URLs directly
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <ImageUploadField
              label="Banner Image"
              file={bannerFile}
              imageUrl={content.banner}
              onImageClick={handleImageClick}
              onImageChange={handleBannerFileSelected}
              onDelete={handleDeleteBanner}
              inputName="banner"
            />
            <TextField
              fullWidth
              label="Banner URL (Alternative)"
              value={content.banner}
              onChange={(e) => handleChange('banner', e.target.value)}
              helperText="Or enter URL directly"
              placeholder="https://example.com/banner.jpg"
              sx={{ mt: 2 }}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ImageUploadField
              label="Video"
              file={videoFile}
              imageUrl={content.videoUrl}
              isVideo={true}
              onImageClick={handleImageClick}
              onImageChange={handleVideoFileSelected}
              onDelete={handleDeleteVideo}
              inputName="video"
            />
            <TextField
              fullWidth
              label="Video URL (Alternative)"
              value={content.videoUrl}
              onChange={(e) => handleChange('videoUrl', e.target.value)}
              helperText="Or enter URL directly"
              placeholder="https://example.com/video.mp4"
              sx={{ mt: 2 }}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ImageUploadField
              label="GIF"
              file={gifFile}
              imageUrl={content.gifUrl}
              onImageClick={handleImageClick}
              onImageChange={handleGifFileSelected}
              onDelete={handleDeleteGif}
              inputName="gif"
            />
            <TextField
              fullWidth
              label="GIF URL (Alternative)"
              value={content.gifUrl}
              onChange={(e) => handleChange('gifUrl', e.target.value)}
              helperText="Or enter URL directly"
              placeholder="https://example.com/animation.gif"
              sx={{ mt: 2 }}
              size="small"
            />
          </Grid>

          {/* Relationships */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Additional Options
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Optionally link this content to additional entities for cross-referencing
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              options={availableTags}
              getOptionLabel={(option) => option.name || ''}
              value={availableTags.find(tag => tag.id === content.tagId) || null}
              onChange={(event, newValue) => handleChange('tagId', newValue?.id || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tag (Optional)"
                  helperText="Optional tag for categorization"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              options={availableSections}
              getOptionLabel={(option) => option.name || ''}
              value={availableSections.find(section => section.id === content.sectionId) || null}
              onChange={(event, newValue) => handleChange('sectionId', newValue?.id || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Section (Optional)"
                  helperText="Optionally link to a section"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              options={availableStyles}
              getOptionLabel={(option) => option.name || ''}
              value={availableStyles.find(style => style.id === content.styleId) || null}
              onChange={(event, newValue) => handleChange('styleId', newValue?.id || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Style (Optional)"
                  helperText="Optionally link to a style"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              options={availablePrompts}
              getOptionLabel={(option) => option.name || 'Unnamed Prompt'}
              value={availablePrompts.find(prompt => prompt.id === content.promptId) || null}
              onChange={(event, newValue) => handleChange('promptId', newValue?.id || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Prompt (Optional)"
                  helperText="Optionally link to a prompt"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>

          {/* Preview */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Preview
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                backgroundColor: '#f5f5f5',
                border: '1px dashed #ccc'
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Content Preview:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  <strong>Type:</strong>
                  <Chip 
                    label={content.contentType} 
                    color={getContentTypeColor(content.contentType)}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Typography variant="body2">
                  <strong>Design:</strong>
                  <Chip 
                    label={content.designType} 
                    color={getDesignTypeColor(content.designType)}
                    size="small"
                    variant="filled"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                {content.contentId && (
                  <Typography variant="body2">
                    <strong>Content ID:</strong> {content.contentId}
                  </Typography>
                )}
                {content.banner && (
                  <Typography variant="body2">
                    <strong>Banner:</strong> Attached
                  </Typography>
                )}
                {content.videoUrl && (
                  <Typography variant="body2">
                    <strong>Video:</strong> Attached
                  </Typography>
                )}
                {content.gifUrl && (
                  <Typography variant="body2">
                    <strong>GIF:</strong> Attached
                  </Typography>
                )}
                {content.tagId && (
                  <Typography variant="body2">
                    <strong>Tag:</strong> {availableTags.find(tag => tag.id === content.tagId)?.name || content.tagId}
                  </Typography>
                )}
                {content.sectionId && (
                  <Typography variant="body2">
                    <strong>Section:</strong> {availableSections.find(section => section.id === content.sectionId)?.name || content.sectionId}
                  </Typography>
                )}
                {content.styleId && (
                  <Typography variant="body2">
                    <strong>Style:</strong> {availableStyles.find(style => style.id === content.styleId)?.name || content.styleId}
                  </Typography>
                )}
                {content.promptId && (
                  <Typography variant="body2">
                    <strong>Prompt:</strong> {availablePrompts.find(prompt => prompt.id === content.promptId)?.name || 'Unnamed Prompt'}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mt: 4, 
          justifyContent: 'flex-end',
          flexWrap: 'wrap'
        }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/content')}
            disabled={progress}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            startIcon={progress ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleCreateContent}
            disabled={progress}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            {progress ? 'Creating...' : 'Create Content'}
          </Button>
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={6000}
        onClose={handleSnackBarClose}
      >
        <Alert onClose={handleSnackBarClose} severity={snackBarSeverity}>
          {snackBarMessage}
        </Alert>
      </Snackbar>

      {/* Image Modal */}
      <Modal
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          sx={{
            maxWidth: '90vw',
            maxHeight: '90vh',
            outline: 'none'
          }}
        >
          <img
            src={modalImageSrc}
            alt="Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain'
            }}
          />
        </Box>
      </Modal>
    </Box>
  )
}
